import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaTimes, FaSpinner, FaSync, FaSearch, FaLaptop, FaMobileAlt, FaUserPlus, FaSignInAlt, FaSignOutAlt,
  FaDoorOpen, FaDoorClosed, FaFileUpload, FaFlagCheckered, FaUnlink, FaListAlt, FaDesktop, FaExclamationTriangle,
} from 'react-icons/fa';
import * as phpApi from '../api/phpApi';
import './ActivityLogModal.css';

const EVENT_INFO = {
  REGISTERED: { label: 'Registered for the examination', icon: FaUserPlus, tone: 'neutral' },
  PORTAL_LOGIN: { label: 'Signed in to the examination portal', icon: FaSignInAlt, tone: 'info' },
  PORTAL_LOGOUT: { label: 'Signed out of the examination portal', icon: FaSignOutAlt, tone: 'muted' },
  EXAM_ENTERED: { label: 'Started the examination and entered the proctoring room', icon: FaDoorOpen, tone: 'success' },
  PROCTORING_LOGOUT: { label: 'Logged out of the proctoring room', icon: FaDoorClosed, tone: 'warning' },
  AUTO_LOGOUT_DEVICE_DISCONNECTED: { label: 'Automatically logged out: the other device stayed disconnected for 2 minutes', icon: FaUnlink, tone: 'danger' },
  LAPTOP_DISCONNECTED: { label: 'Laptop disconnected / camera stopped', icon: FaLaptop, tone: 'danger' },
  LAPTOP_RECONNECTED: { label: 'Laptop reconnected', icon: FaLaptop, tone: 'success' },
  MOBILE_DISCONNECTED: { label: 'Mobile disconnected / camera stopped', icon: FaMobileAlt, tone: 'danger' },
  MOBILE_RECONNECTED: { label: 'Mobile reconnected', icon: FaMobileAlt, tone: 'success' },
  TAB_SWITCHED_AWAY: { label: 'Tab switched away / app minimized', icon: FaDesktop, tone: 'warning' },
  TAB_RETURNED: { label: 'Returned to examination tab', icon: FaDesktop, tone: 'success' },
  CONNECTION_LOST: { label: 'Internet / socket connection lost', icon: FaUnlink, tone: 'danger' },
  CONNECTION_RESTORED: { label: 'Internet / socket reconnected', icon: FaSync, tone: 'success' },
  FULLSCREEN_EXITED: { label: 'Exited fullscreen mode', icon: FaExclamationTriangle, tone: 'warning' },
  FULLSCREEN_RESTORED: { label: 'Restored fullscreen mode', icon: FaDesktop, tone: 'success' },
  ANSWER_SCRIPT_SUBMITTED: { label: 'Answer script submitted', icon: FaFileUpload, tone: 'success' },
  EXAM_SESSION_ENDED: { label: 'Examination session ended', icon: FaFlagCheckered, tone: 'muted' },
};

const DEVICE_LABEL = { laptop: 'Laptop / Desktop', mobile: 'Mobile' };

const pad = (n) => String(n).padStart(2, '0');

// Server timestamps are 'YYYY-MM-DD HH:MM:SS' in the institution's local time.
const formatDateTime = (value) => {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return value || '—';
  const h = parseInt(m[4], 10);
  return `${m[3]}-${m[2]}-${m[1]}  ${pad(h % 12 || 12)}:${m[5]}:${m[6] || '00'} ${h >= 12 ? 'PM' : 'AM'}`;
};

const ActivityLogModal = ({ roomId, roomUsers, onClose }) => {
  const [students, setStudents] = useState([]);
  const [examInfo, setExamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await phpApi.getExamActivityLogs({ room_id: roomId });
      if (res?.status !== 'success') throw new Error(res?.message || 'Could not load the activity log.');
      setExamInfo(res?.exam || null);
      const list = Array.isArray(res.data) ? res.data : [];
      setStudents(list);
      setSelectedId((prev) => (prev != null && list.some((s) => s.registration_id === prev) ? prev : list[0]?.registration_id ?? null));
    } catch (err) {
      setError(err?.message || 'Could not load the activity log.');
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Live presence of each student's devices in the proctoring room (socket user id = student_id).
  const presence = useMemo(() => {
    const map = new Map();
    (roomUsers || []).forEach((u) => {
      if (u.userType !== 'student') return;
      const key = String(u.userId);
      const entry = map.get(key) || { laptop: false, mobile: false };
      if (u.isMobile) entry.mobile = !u.isAway; else entry.laptop = !u.isAway;
      map.set(key, entry);
    });
    return map;
  }, [roomUsers]);

  const numbered = useMemo(() => students.map((s, i) => ({ ...s, serial: String(i + 1).padStart(3, '0') })), [students]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return numbered;
    return numbered.filter((s) => `${s.serial} ${s.register_number || ''} ${s.student_name || ''}`.toLowerCase().includes(q));
  }, [numbered, query]);

  const selected = numbered.find((s) => s.registration_id === selectedId) || null;

  return (
    <div className="violation-modal-backdrop" onClick={onClose}>
      <div className="activity-log-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Student activity log">
        <div className="violation-modal-header">
          <div>
            <h3><FaListAlt style={{ marginRight: 8, verticalAlign: '-2px' }} />Student Activity Log</h3>
            {examInfo && (examInfo.course || examInfo.exam_date || examInfo.exam_time) && (
              <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px', fontWeight: '500' }}>
                {examInfo.course && <span><strong>Subject:</strong> {examInfo.course}</span>}
                {examInfo.exam_date && <span style={{ marginLeft: '12px' }}><strong>Date:</strong> {examInfo.exam_date}</span>}
                {examInfo.exam_time && <span style={{ marginLeft: '12px' }}><strong>Slot:</strong> {examInfo.exam_time}</span>}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="violation-modal-close" onClick={load} title="Refresh" disabled={loading}>
              <FaSync className={loading ? 'spinner' : ''} />
            </button>
            <button type="button" className="violation-modal-close" onClick={onClose} title="Close"><FaTimes /></button>
          </div>
        </div>

        {error && <div className="activity-log-error">{error}</div>}

        <div className="activity-log-body">
          <aside className="activity-log-students">
            <div className="activity-log-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by no., register no. or name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="activity-log-count">{students.length} registered student{students.length === 1 ? '' : 's'}</div>
            <ul>
              {loading && students.length === 0 && (
                <li className="activity-log-placeholder"><FaSpinner className="spinner" /> Loading…</li>
              )}
              {!loading && filtered.length === 0 && (
                <li className="activity-log-placeholder">No students found.</li>
              )}
              {filtered.map((s) => {
                const live = presence.get(String(s.student_id)) || {};
                return (
                  <li
                    key={s.registration_id}
                    className={s.registration_id === selectedId ? 'selected' : ''}
                    onClick={() => setSelectedId(s.registration_id)}
                  >
                    <span className="activity-log-serial">{s.serial}</span>
                    <span className="activity-log-student">
                      <strong>{s.register_number || '—'}</strong>
                      <small>{s.student_name || 'Student'}</small>
                    </span>
                    <span className="activity-log-flags">
                      <FaLaptop className={live.laptop ? 'on' : ''} title={live.laptop ? 'Laptop connected' : 'Laptop not connected'} />
                      <FaMobileAlt className={live.mobile ? 'on' : ''} title={live.mobile ? 'Mobile connected' : 'Mobile not connected'} />
                      {s.exam_completed === 1 && <span className="activity-log-badge">Submitted</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="activity-log-timeline">
            {!selected ? (
              <p className="violation-modal-empty">{loading ? 'Loading…' : 'Select a student to view the activity log.'}</p>
            ) : (
              <>
                <div className="activity-log-timeline-head">
                  <span className="activity-log-serial">{selected.serial}</span>
                  <div>
                    <strong>{selected.student_name || 'Student'}</strong>
                    <small>Register No: {selected.register_number || '—'}</small>
                  </div>
                </div>
                {selected.events.length === 0 ? (
                  <p className="violation-modal-empty">No activity recorded yet.</p>
                ) : (
                  <ol>
                    {selected.events.map((ev, i) => {
                      const info = EVENT_INFO[ev.type] || { label: ev.type, icon: FaListAlt, tone: 'neutral' };
                      const Icon = info.icon;
                      const DeviceIcon = ev.device === 'mobile' ? FaMobileAlt : FaLaptop;
                      return (
                        <li key={`${ev.type}-${ev.at}-${i}`} className={`tone-${info.tone}`}>
                          <span className="activity-log-dot"><Icon /></span>
                          <div className="activity-log-entry">
                            <div className="activity-log-entry-title">{info.label}</div>
                            <div className="activity-log-entry-meta">
                              <span>{formatDateTime(ev.at)}</span>
                              {ev.device && (
                                <span className="activity-log-device"><DeviceIcon /> {DEVICE_LABEL[ev.device] || ev.device}</span>
                              )}
                              {ev.meta && <span className="activity-log-extra">{ev.meta}</span>}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogModal;
